import { cloneDeep, compact, countBy, get, set, uniq } from 'lodash-es';
import { toBig } from '../../utils/number';
import {
  isNotDivider,
  type NonDividerDrawerOption,
} from './components/InputsAndDropdownsDrawer/components/dividerUtils';
import { flattenDrawerOptions } from './components/InputsAndDropdownsDrawer/filterAndMapDrawerOptions';
import type { InputsAndDropdownsDrawerOptions } from './components/InputsAndDropdownsDrawer/types';
import type { EntityAdminClass } from './EntityAdminClass';
import { getFormErrorState, getInitialFormState, prepareFormStateForSave } from './hooks/useEntityAdminFormState';
import type { BaseEntityAdminProps, EntityAdminRecord } from './types';
import { getEntityRowId } from './utils';

/** Drawer option types a CSV cell can fill in. */
const BULK_UPLOAD_OPTION_TYPES = new Set<NonDividerDrawerOption<EntityAdminRecord>['type']>([
  'input',
  'inputNumeric',
  'dropdown',
  'toggle',
  'symbolSelector',
]);

export interface EntityAdminBulkUploadRow<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord> {
  rowID: string;
  /** Undefined when the row has a warning, as it will not be imported. */
  action: 'Create' | 'Update' | undefined;
  warning: string | undefined;
  /** Nested CSV cells by drawer field path. Updates only apply `providedFields`. */
  values: EntityAdminRecord;
  providedFields: string[];
  entity: TDrawerRecord;
  existingEntity: EntityAdminClass<TRecord> | undefined;
}

export type EntityAdminBulkUploadParams<
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord,
> = Pick<BaseEntityAdminProps<TRecord, TDrawerRecord>, 'entityIDField' | 'childIDField' | 'newEntityDefault'> & {
  /** Every loaded entity by row ID, including rows hidden by client-side filters. */
  loadedEntities: ReadonlyMap<string, EntityAdminClass<TRecord>>;
  getDrawerOptions: (
    entity: TDrawerRecord | undefined,
    addingChildEntity: boolean
  ) => InputsAndDropdownsDrawerOptions<TDrawerRecord>;
  getEntityAsDrawerEntity: (entity: TRecord, classEntity?: EntityAdminClass<TRecord>) => TDrawerRecord;
};

/** The values a choice field accepts, or undefined when any text is accepted. */
function getAllowedValues<TDrawerRecord extends EntityAdminRecord>(
  option: NonDividerDrawerOption<TDrawerRecord>,
  form: TDrawerRecord,
  isEditing: boolean
): unknown[] | undefined {
  switch (option.type) {
    case 'dropdown':
      return option.getDropdownOptions(form, isEditing).map(({ value }) => value);
    case 'toggle':
      return option.getToggleOptions(form, isEditing).map(({ value }) => value);
    case 'symbolSelector':
      return option
        .getAvailableSecurities?.(form, isEditing)
        .filter(security => !option.getIsItemDisabled?.(security, form, isEditing))
        .map(({ Symbol }) => Symbol);
    default:
      return undefined;
  }
}

/** CSV columns: the ID fields, then the text and choice fields of the create drawers. */
export function getEntityAdminBulkUploadFields<
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord,
>({
  entityIDField,
  childIDField,
  newEntityDefault,
  getDrawerOptions,
  getEntityAsDrawerEntity,
}: EntityAdminBulkUploadParams<TRecord, TDrawerRecord>): string[] {
  const getCreateDrawerFields = (addingChildEntity: boolean) => {
    const drawerOptions = flattenDrawerOptions(getDrawerOptions(undefined, addingChildEntity)).filter(isNotDivider);
    const createForm = getInitialFormState({
      selectedEntities: [],
      flatDrawerOptions: drawerOptions,
      getEntityAsDrawerEntity,
      newEntityDefault,
      addingChildEntity,
    });
    return drawerOptions
      .filter(option => BULK_UPLOAD_OPTION_TYPES.has(option.type) && !option.getIsHidden?.(createForm))
      .map(option => option.field);
  };
  const drawerFields =
    childIDField == null
      ? getCreateDrawerFields(false)
      : [...getCreateDrawerFields(false), ...getCreateDrawerFields(true)];
  return uniq(compact([entityIDField, childIDField, ...drawerFields]));
}

function getCsvCell(values: EntityAdminRecord, field: string): string | undefined {
  const value = get(values, field);
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Validates each CSV row like the drawer would and builds the entity to POST (Create) or PATCH (Update). */
export function getEntityAdminBulkUploadRows<
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord,
>(
  csvRows: Record<string, string | undefined>[],
  params: EntityAdminBulkUploadParams<TRecord, TDrawerRecord>
): EntityAdminBulkUploadRow<TRecord, TDrawerRecord>[] {
  const fields = getEntityAdminBulkUploadFields(params);
  const rowValues = csvRows.map(csvRow => {
    const values: EntityAdminRecord = {};
    fields.forEach(field => {
      const value = csvRow[field]?.trim();
      if (value) {
        set(values, field, value);
      }
    });
    return values;
  });
  const rowEntityIDs = rowValues.map(values =>
    getEntityRowId<EntityAdminRecord>(values, params.entityIDField, params.childIDField)
  );
  const rowEntityIDCounts = countBy(rowEntityIDs);

  return rowValues.map((values, index) => {
    const rowEntityID = rowEntityIDs[index];
    return getEntityAdminBulkUploadRow(
      values,
      String(index),
      rowEntityID,
      rowEntityIDCounts[rowEntityID] > 1,
      fields,
      params
    );
  });
}

function getEntityAdminBulkUploadRow<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>(
  values: EntityAdminRecord,
  rowID: string,
  rowEntityID: string,
  isDuplicate: boolean,
  fields: string[],
  {
    entityIDField,
    childIDField,
    newEntityDefault,
    loadedEntities,
    getDrawerOptions,
    getEntityAsDrawerEntity,
  }: EntityAdminBulkUploadParams<TRecord, TDrawerRecord>
): EntityAdminBulkUploadRow<TRecord, TDrawerRecord> {
  const providedFields = fields.filter(field => getCsvCell(values, field) != null);
  const existingEntity = loadedEntities.get(rowEntityID);
  const isEditing = existingEntity != null;
  const addingChildEntity = !isEditing && childIDField != null && getCsvCell(values, childIDField) != null;
  const parentEntity = addingChildEntity
    ? loadedEntities.get(getEntityRowId<EntityAdminRecord>(values, entityIDField))
    : undefined;
  const originalEntity = existingEntity && getEntityAsDrawerEntity(existingEntity.data, existingEntity);
  const drawerOptions = flattenDrawerOptions(getDrawerOptions(originalEntity, addingChildEntity)).filter(isNotDivider);

  // Start from the form the drawer would open with, then type in the CSV cells.
  const initialForm = getInitialFormState({
    selectedEntities: compact([existingEntity ?? parentEntity]),
    flatDrawerOptions: drawerOptions,
    getEntityAsDrawerEntity,
    newEntityDefault,
    addingChildEntity,
  });
  const form = cloneDeep(initialForm);
  providedFields.forEach(field => set(form, field, getCsvCell(values, field)));

  const errors: Record<string, string> = {};
  providedFields.forEach(field => {
    const value = getCsvCell(values, field);
    if (value == null) {
      return;
    }
    const option = drawerOptions.find(drawerOption => drawerOption.field === field);
    const isLocked =
      option == null ||
      !BULK_UPLOAD_OPTION_TYPES.has(option.type) ||
      option.getIsHidden?.(form, originalEntity) ||
      (option.disabledWhenEditing && isEditing) ||
      option.getIsDisabled?.(form, originalEntity);
    const isIDField = field === entityIDField || field === childIDField;
    const allowedValues = option && getAllowedValues(option, form, isEditing);
    const allowedValue = allowedValues?.find(allowed => String(allowed) === value);

    if (isLocked && !isIDField && value !== String(get(initialForm, field) ?? '')) {
      errors[field] = 'Cannot be changed';
    } else if (option?.type === 'inputNumeric' && toBig(value) == null) {
      errors[field] = 'Not a valid number';
    } else if (allowedValues != null && allowedValue == null && !(isEditing && isIDField)) {
      errors[field] = 'Not a valid option';
    } else if (allowedValue != null) {
      set(form, field, allowedValue);
    }
  });

  if (!getCsvCell(values, entityIDField)) {
    errors[entityIDField] = 'This field is required';
  } else if (isDuplicate) {
    errors[entityIDField] = 'Duplicate row in file';
  } else if (addingChildEntity && parentEntity == null) {
    errors[entityIDField] = 'Parent not found';
  }

  const warnings = { ...getFormErrorState(drawerOptions, form, originalEntity, undefined, isEditing), ...errors };
  const warning = compact(Object.entries(warnings).map(([field, error]) => error && `${field}: ${error}`)).join('; ');

  return {
    rowID,
    action: warning ? undefined : isEditing ? 'Update' : 'Create',
    warning: warning || undefined,
    values,
    providedFields,
    entity: prepareFormStateForSave(form, drawerOptions),
    existingEntity,
  };
}
